import * as React from 'react'
import { AlertTriangle, Clock, CloudOff, Inbox, Lock, RefreshCw, ShieldAlert } from 'lucide-react'
import { ApiError } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/controls'
import { cn } from '@/lib/utils'

/**
 * The states every screen handles (screen spec §2): loading, empty, denied,
 * unavailable, stale, partial, pending approval, success, failed.
 * `QueryState` picks the right one for a TanStack query.
 */

function StateBox({ icon, title, children, action, tone = 'neutral', className }: { icon: React.ReactNode; title: string; children?: React.ReactNode; action?: React.ReactNode; tone?: 'neutral' | 'red' | 'amber'; className?: string }) {
  return (
    <div className={cn('flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-line bg-surface px-6 py-12 text-center', className)}>
      <div className={cn('grid size-10 place-items-center rounded-full [&_svg]:size-5', tone === 'red' ? 'bg-red/10 text-red' : tone === 'amber' ? 'bg-amber/10 text-amber' : 'bg-soft text-muted')}>{icon}</div>
      <h3 className="text-[13.5px] font-semibold text-ink">{title}</h3>
      {children && <div className="max-w-[440px] text-[12px] leading-relaxed text-muted">{children}</div>}
      {action && <div className="mt-2 flex flex-wrap justify-center gap-2">{action}</div>}
    </div>
  )
}

export function LoadingState({ rows = 4, className }: { rows?: number; className?: string }) {
  return (
    <div className={cn('grid gap-3', className)} role="status" aria-label="Loading">
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} className="h-10 w-full" />
      ))}
    </div>
  )
}

export function EmptyState({ title, children, action, icon }: { title: string; children?: React.ReactNode; action?: React.ReactNode; icon?: React.ReactNode }) {
  return (
    <StateBox icon={icon ?? <Inbox />} title={title} action={action}>
      {children}
    </StateBox>
  )
}

/** Denied: never names or counts the hidden thing. */
export function DeniedState({ what = 'this' }: { what?: string }) {
  return (
    <StateBox icon={<Lock />} title="You don’t have access" tone="amber">
      Your role doesn’t include {what}. Ask an organisation admin if you need it.
    </StateBox>
  )
}

export function UnavailableState({ message, onRetry }: { message?: string; onRetry?: () => void }) {
  return (
    <StateBox icon={<CloudOff />} title="This isn’t available right now" tone="red" action={onRetry && <Button size="sm" onClick={onRetry}><RefreshCw />Try again</Button>}>
      {message ?? 'ESM couldn’t load this. Nothing was changed. Try again in a moment.'}
    </StateBox>
  )
}

export function NotFoundState({ what = 'This item', action }: { what?: string; action?: React.ReactNode }) {
  return (
    <StateBox icon={<AlertTriangle />} title={`${what} wasn’t found`} action={action}>
      It may have been removed, or you may not have access to it.
    </StateBox>
  )
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  if (error instanceof ApiError) {
    if (error.isDenied) return <DeniedState />
    if (error.isNotFound) return <NotFoundState />
    if (error.isUnavailable) return <UnavailableState message={error.message} onRetry={onRetry} />
    return (
      <StateBox icon={<ShieldAlert />} title="That didn’t work" tone="red" action={onRetry && <Button size="sm" onClick={onRetry}>Try again</Button>}>
        {error.message}
      </StateBox>
    )
  }
  return <UnavailableState onRetry={onRetry} />
}

/** Inline notices: partial results, stale data, pending approval, demo/limits. */
export function Notice({ tone = 'neutral', title, children, action, icon }: { tone?: 'neutral' | 'amber' | 'red' | 'green' | 'accent'; title?: React.ReactNode; children?: React.ReactNode; action?: React.ReactNode; icon?: React.ReactNode }) {
  const tones = {
    neutral: 'border-line bg-soft text-ink',
    amber: 'border-amber/30 bg-amber/8 text-ink',
    red: 'border-red/30 bg-red/8 text-ink',
    green: 'border-green/30 bg-green/8 text-ink',
    accent: 'border-accent/30 bg-accent-soft text-ink',
  }
  const iconTone = { neutral: 'text-muted', amber: 'text-amber', red: 'text-red', green: 'text-green', accent: 'text-accent' }
  return (
    <div role={tone === 'red' ? 'alert' : 'status'} className={cn('flex items-start gap-3 rounded-xl border px-3.5 py-3 text-[12px] leading-relaxed', tones[tone])}>
      {icon !== null && <span className={cn('mt-0.5 shrink-0 [&_svg]:size-4', iconTone[tone])}>{icon ?? <AlertTriangle />}</span>}
      <div className="min-w-0 flex-1">
        {title && <div className="font-semibold">{title}</div>}
        {children && <div className={cn(title && 'mt-0.5', 'text-muted')}>{children}</div>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  )
}

export const PartialNotice = ({ children }: { children: React.ReactNode }) => <Notice tone="amber" title="Some results are missing">{children}</Notice>
export const PendingApprovalNotice = ({ children }: { children: React.ReactNode }) => (
  <Notice tone="accent" icon={<Clock />} title="Waiting for approval">
    {children}
  </Notice>
)

/** Engine wording that must never be primary UI text (it comes through raw in some server details). */
const ENGINE_JARGON = /\b(dense arm|lexical arm|kNN|hash vectors?|non-semantic space|BM25)\b/i
export const isTechnicalText = (t: string | null | undefined): boolean => !!t && ENGINE_JARGON.test(t)

/** Server text shown as-is, unless it is engine jargon: then a plain sentence, with the raw text behind a disclosure. */
export function PlainDetail({ text, fallback = 'Part of the search did not run as expected, so fewer sources contributed.', className }: { text: string; fallback?: string; className?: string }) {
  if (!isTechnicalText(text)) return <p className={className}>{text}</p>
  return (
    <div className="grid gap-1">
      <p className={className}>{fallback}</p>
      <TechnicalDetail lines={[text]} />
    </div>
  )
}

/** Technical text (engine errors, provider names) kept out of the primary UI, one click away for support. */
export function TechnicalDetail({ lines }: { lines: Array<string | null | undefined> }) {
  const shown = [...new Set(lines.filter((l): l is string => !!l && !!l.trim()))]
  if (!shown.length) return null
  return (
    <details className="text-[11px] text-muted">
      <summary className="cursor-pointer select-none hover:text-ink">Technical detail</summary>
      <div className="mt-1 grid gap-1 rounded-md bg-soft p-2 font-mono text-[10.5px] leading-relaxed break-words">
        {shown.map(l => <p key={l}>{l}</p>)}
      </div>
    </details>
  )
}

/**
 * Render a TanStack query by state. `isEmpty` decides empty vs data.
 * Usage: <QueryState q={q} empty={<EmptyState …/>} isEmpty={d => d.items.length === 0}>{d => …}</QueryState>
 */
export function QueryState<T>({
  q,
  children,
  empty,
  isEmpty,
  loading,
}: {
  q: { data: T | undefined; error: unknown; isPending: boolean; refetch: () => unknown }
  children: (data: T) => React.ReactNode
  empty?: React.ReactNode
  isEmpty?: (data: T) => boolean
  loading?: React.ReactNode
}) {
  if (q.isPending) return <>{loading ?? <LoadingState />}</>
  if (q.error) return <ErrorState error={q.error} onRetry={() => q.refetch()} />
  if (q.data === undefined) return <>{loading ?? <LoadingState />}</>
  if (empty && isEmpty?.(q.data)) return <>{empty}</>
  return <>{children(q.data)}</>
}
